import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { clsx } from 'clsx';

import { Dropdown, Icon, TDropdownItem } from '@treezor/design-system';

import { useToggleDropdown } from '@/hooks/useToggleDropdown';
import { useAppSelector } from '@/hooks/redux-helpers';

export interface IUserMenuProps {
  userName: string;
  onLogout: () => void;
  onViewAccount: () => void;
}

export function UserMenu({ userName, onLogout, onViewAccount }: IUserMenuProps) {
  const { ref, isOpen: isMenuOpen, setOpen } = useToggleDropdown();

  const { t } = useTranslation();
  const is2FAEnabled = useAppSelector((state) => !!state.dashboardConfig.config.settings['2fa']);

  const menuItems: TDropdownItem[] = useMemo(
    () => [
      ...(is2FAEnabled
        ? [
            {
              icon: 'account_circle',
              label: t('components.molecules.userMenu.viewAccount'),
              onClick: onViewAccount,
            } as const,
          ]
        : []),
      { icon: 'logout', label: t('components.molecules.userMenu.signOut'), onClick: onLogout },
    ],
    [is2FAEnabled, onLogout, onViewAccount, t],
  );

  return (
    <div className="relative">
      <div
        className="flex justify-between hover:cursor-pointer group items-center gap-2"
        onClick={() => setOpen((open) => !open)}
        ref={ref}
      >
        <span
          className={clsx(
            'typo-body-semibold-paragraph group-hover:text-interactive text-nowrap',
            isMenuOpen ? 'text-interactive' : 'text-secondary',
          )}
        >
          {userName}
        </span>

        <Icon
          name="expand_more"
          size="s"
          className={clsx(
            'group-hover:text-icon-interactive transition-transform',
            isMenuOpen && 'rotate-180 text-icon-interactive',
          )}
        />
      </div>

      {isMenuOpen && (
        <Dropdown onClose={() => setOpen(false)} parentRef={ref} className="!min-w-[150px]" items={menuItems} />
      )}
    </div>
  );
}
